import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Move, calculate } from '@smogon/calc';
import { calcStat, buildPokemon, buildField, STATUS_MAP, NATURE_STAT_MAP, QP_ABILITIES } from '../utils/calcCore';
import { pokemonList } from '../data/pokemonList';
import { POKEMON_DATA } from '../data/pokemonData';
import {
  COMMON_SETS, NATURE_TABLE, NATURES,
  ITEM_GROUPS, WEATHER_OPTIONS, TYPES,
} from '../data/commonSets';
import { MOVE_DATA, STATUS_MOVES, ALL_MOVE_NAMES } from '../data/competitiveMoves';
import { generateSpreads } from '../utils/evOptimizer';
import { getGen, toShowdownName, statLabel } from '../utils/showdownHelpers';
import './QuickCalc.css';

// ── Constants ─────────────────────────────────────────────────────────────────

// pokemonList.js uses base-form names (e.g. "tornadus") while pokemonData.js stores
// the PokeAPI form names (e.g. "tornadus-incarnate"). This map resolves the mismatch
// so selecting any Pokemon in search always finds its POKEMON_DATA entry.
const POKEMON_DATA_KEY_MAP = {
  'tornadus':           'tornadus-incarnate',
  'thundurus':          'thundurus-incarnate',
  'landorus':           'landorus-incarnate',
  'enamorus':           'enamorus-incarnate',
  'urshifu':            'urshifu-single-strike',
  'giratina':           'giratina-altered',
  'darmanitan':         'darmanitan-standard',
  'deoxys':             'deoxys-normal',
  'wormadam':           'wormadam-plant',
  'shaymin':            'shaymin-land',
  'keldeo':             'keldeo-ordinary',
  'meloetta':           'meloetta-aria',
  'meowstic':           'meowstic-male',
  'aegislash':          'aegislash-shield',
  'pumpkaboo':          'pumpkaboo-average',
  'gourgeist':          'gourgeist-average',
  'zygarde':            'zygarde-50',
  'lycanroc':           'lycanroc-midday',
  'wishiwashi':         'wishiwashi-solo',
  'minior':             'minior-red-meteor',
  'mimikyu':            'mimikyu-disguised',
  'toxtricity':         'toxtricity-amped',
  'eiscue':             'eiscue-ice',
  'indeedee':           'indeedee-male',
  'indeedee-f':         'indeedee-female',
  'morpeko':            'morpeko-full-belly',
  'basculin':           'basculin-red-striped',
  'basculegion':        'basculegion-male',
  'maushold':           'maushold-family-of-three',
  'squawkabilly':       'squawkabilly-green-plumage',
  'palafin':            'palafin-zero',
  'tatsugiri':          'tatsugiri-curly',
  'dudunsparce':        'dudunsparce-two-segment',
  'oinkologne':         'oinkologne-male',
  'oricorio':           'oricorio-baile',
  'necrozma-dusk-mane': 'necrozma-dusk',
  'necrozma-dawn-wings':'necrozma-dawn',
};

// Returns the canonical pokemonData key (resolves base names → specific form names)
function resolveKey(key) {
  return POKEMON_DATA_KEY_MAP[key] || key;
}

const STAT_KEYS = ['hp', 'atk', 'def', 'spa', 'spd', 'spe'];
const STATUS_OPTIONS = ['', 'Burned', 'Paralyzed', 'Poisoned', 'Badly Poisoned', 'Asleep', 'Frozen'];
const SMOGON_TO_LABEL = { attack: 'Atk', defense: 'Def', specialAttack: 'SpA', specialDefense: 'SpD', speed: 'Spe' };

const TYPE_COLORS = {
  Normal: '#a8a878', Fire: '#f08030', Water: '#6890f0',
  Electric: '#f8d030', Grass: '#78c850', Ice: '#98d8d8',
  Fighting: '#c03028', Poison: '#a040a0', Ground: '#e0c068',
  Flying: '#a890f0', Psychic: '#f85888', Bug: '#a8b820',
  Rock: '#b8a038', Ghost: '#705898', Dragon: '#7038f8',
  Dark: '#705848', Steel: '#b8b8d0', Fairy: '#f0b6bc',
};

const STAT_BAR_COLORS = {
  hp: '#ff5959', atk: '#f5ac78', def: '#fae078',
  spa: '#9db7f5', spd: '#a7db8d', spe: '#fa92b2',
};
const ALL_POKEMON_NAMES = Object.keys(pokemonList);
const WEATHER_BTNS = WEATHER_OPTIONS.filter(w => w !== 'None');

// ── Mega Evolution support ─────────────────────────────────────────────────────
// Maps each Mega pokemonData key → its required Mega Stone name.
// Standard Pokémon game Megas use canonical stone names; Pokémon Champions
// exclusives use "[Name] Mega Stone" as a placeholder (item has no calc effect
// in Gen 9 since stats are fully overridden, but it communicates the requirement).
const MEGA_STONE_MAP = {
  // Gen 1 origins
  'venusaur-mega':     'Venusaurite',
  'charizard-mega-x':  'Charizardite X',
  'charizard-mega-y':  'Charizardite Y',
  'blastoise-mega':    'Blastoisinite',
  'alakazam-mega':     'Alakazite',
  'gengar-mega':       'Gengarite',
  'kangaskhan-mega':   'Kangaskhanite',
  'pinsir-mega':       'Pinsirite',
  'gyarados-mega':     'Gyaradosite',
  'aerodactyl-mega':   'Aerodactylite',
  'mewtwo-mega-x':     'Mewtwonite X',
  'mewtwo-mega-y':     'Mewtwonite Y',
  // Gen 2 origins
  'ampharos-mega':     'Ampharosite',
  'scizor-mega':       'Scizorite',
  'heracross-mega':    'Heracronite',
  'houndoom-mega':     'Houndoominite',
  'tyranitar-mega':    'Tyranitarite',
  // Gen 3 origins
  'blaziken-mega':     'Blazikenite',
  'gardevoir-mega':    'Gardevoirite',
  'mawile-mega':       'Mawilite',
  'aggron-mega':       'Aggronite',
  'medicham-mega':     'Medichamite',
  'manectric-mega':    'Manectite',
  'banette-mega':      'Banettite',
  'absol-mega':        'Absolite',
  'latias-mega':       'Latiasite',
  'latios-mega':       'Latiosite',
  'swampert-mega':     'Swampertite',
  'sceptile-mega':     'Sceptilite',
  'sableye-mega':      'Sablenite',
  'altaria-mega':      'Altarianite',
  'salamence-mega':    'Salamencite',
  'metagross-mega':    'Metagrossite',
  'rayquaza-mega':     'None', // needs Dragon Ascent, no stone
  'camerupt-mega':     'Cameruptite',
  'sharpedo-mega':     'Sharpedonite',
  // Gen 4 origins
  'lucario-mega':      'Lucarionite',
  'abomasnow-mega':    'Abomasite',
  'garchomp-mega':     'Garchompite',
  'gallade-mega':      'Galladite',
  'lopunny-mega':      'Lopunnite',
  // Gen 5 origins
  'audino-mega':       'Audinite',
  // Gen 6 origins
  'slowbro-mega':      'Slowbronite',
  'steelix-mega':      'Steelixite',
  'pidgeot-mega':      'Pidgeotite',
  'glalie-mega':       'Glalitite',
  'diancie-mega':      'Diancite',
  'beedrill-mega':     'Beedrillite',
  // Champions/ZA-exclusive Megas
  'clefable-mega':     'Clefablite',
  'victreebel-mega':   'Victreebelite',
  'starmie-mega':      'Starminite',
  'dragonite-mega':    'Dragoninite',
  'meganium-mega':     'Meganiumite',
  'feraligatr-mega':   'Feraligite',
  'skarmory-mega':     'Skarmorite',
  'froslass-mega':     'Froslassite',
  'emboar-mega':       'Emboarite',
  'excadrill-mega':    'Excadrite',
  'scolipede-mega':    'Scolipite',
  'scrafty-mega':      'Scraftinite',
  'eelektross-mega':   'Eelektrossite',
  'chandelure-mega':   'Chandelurite',
  'chesnaught-mega':   'Chesnaughtite',
  'delphox-mega':      'Delphoxite',
  'greninja-mega':     'Greninjite',
  'pyroar-mega':       'Pyroarite',
  'floette-mega':      'Floettite',
  'malamar-mega':      'Malamarite',
  'barbaracle-mega':   'Barbaracite',
  'dragalge-mega':     'Dragalgite',
  'hawlucha-mega':     'Hawluchanite',
  'zygarde-mega':      'Zygardite',
  'drampa-mega':       'Drampanite',
  'falinks-mega':      'Falinksite',
  'raichu-mega-x':     'Raichunite X',
  'raichu-mega-y':     'Raichunite Y',
  'chimecho-mega':     'Chimechite',
  'absol-mega-z':      'Absolite Z',
  'staraptor-mega':    'Staraptite',
  'garchomp-mega-z':   'Garchompite Z',
  'lucario-mega-z':    'Lucarionite Z',
  'heatran-mega':      'Heatranite',
  'darkrai-mega':      'Darkranite',
  'golurk-mega':       'Golurkite',
  'meowstic-mega':     'Meowsticite',
  'crabominable-mega': 'Crabominite',
  'golisopod-mega':    'Golisopite',
  'magearna-mega':     'Magearnite',
  'zeraora-mega':      'Zerorite',
  'scovillain-mega':   'Scovillainite',
  'glimmora-mega':     'Glimmoranite',
  'tatsugiri-curly-mega':    'Tatsugirinite',
  'tatsugiri-droopy-mega':   'Tatsugirinite',
  'tatsugiri-stretchy-mega': 'Tatsugirinite',
  'baxcalibur-mega':   'Baxcalibrite',
};

// All Mega keys available in POKEMON_DATA (filters out yanmega/meganium false positives)
const ALL_MEGA_KEYS = Object.keys(POKEMON_DATA).filter(k => k.includes('-mega'));
// Helper: is this pokemonData key a Mega?
function isMegaKey(key) { return key.includes('-mega'); }
// Get the locked Mega Stone for a key (returns null if not a mega)
function getMegaStone(key) {
  if (!isMegaKey(key)) return null;
  return MEGA_STONE_MAP[key] || 'Mega Stone';
}
const MAX_DEFENDERS = 4;

const REGULATIONS = [
  { key: 'reg-g', label: 'Reg G' },
  { key: 'reg-h', label: 'Reg H' },
  { key: 'reg-i', label: 'Reg I' },
  { key: 'open',  label: 'Open'  },
];
// Effects that can be independently applied to either or both sides.
// Each pill renders as  ◄ Label ►  where ◄ = your side, ► = opp side.
const SIDED_EFFECTS = [
  { key: 'isReflect',       label: 'Reflect',         tip: 'Halves physical damage taken for 5 turns' },
  { key: 'isLightScreen',   label: 'Light Screen',    tip: 'Halves special damage taken for 5 turns' },
  { key: 'isAuroraVeil',    label: 'Aurora Veil',     tip: 'Halves both physical and special damage for 5 turns (requires snow/hail)' },
  { key: 'isHelpingHand',   label: 'Helping Hand',    tip: "Partner boosts this side's move by ×1.5" },
  { key: 'isFriendGuard',   label: 'Friend Guard',    tip: 'Partner reduces damage this side takes by 25%' },
  { key: 'isCharged',       label: 'Charged',         tip: 'Next Electric-type move deals double damage (after using Charge)' },
  { key: 'isTailwind',      label: 'Tailwind',        tip: 'Doubles Speed for this side for 4 turns' },
  { key: 'isTabletsOfRuin', label: 'Tablets of Ruin', tip: "Ting-Lu on this side — all other Pokémon's Attack −25%" },
  { key: 'isSwordOfRuin',   label: 'Sword of Ruin',   tip: "Chi-Yu on this side — all other Pokémon's Sp. Def −25%" },
  { key: 'isVesselOfRuin',  label: 'Vessel of Ruin',  tip: "Wo-Chien on this side — all other Pokémon's Sp. Atk −25%" },
  { key: 'isBeadsOfRuin',   label: 'Beads of Ruin',   tip: "Chien-Pao on this side — all other Pokémon's Defense −25%" },
];

// ── Move pool fetching ────────────────────────────────────────────────────────
// PokeAPI version groups for each generation — used to filter moves to those
// actually learnable in the target gen (prevents old-gen-only moves bleeding in)
const GEN_VERSION_GROUPS = {
  1: new Set(['red-blue', 'yellow']),
  2: new Set(['gold-silver', 'crystal']),
  3: new Set(['ruby-sapphire', 'emerald', 'firered-leafgreen']),
  4: new Set(['diamond-pearl', 'platinum', 'heartgold-soulsilver']),
  5: new Set(['black-white', 'black-2-white-2']),
  6: new Set(['x-y', 'omega-ruby-alpha-sapphire']),
  7: new Set(['sun-moon', 'ultra-sun-ultra-moon', 'lets-go-pikachu-lets-go-eevee']),
  8: new Set(['sword-shield', 'brilliant-diamond-and-shining-pearl', 'legends-arceus']),
  9: new Set(['scarlet-violet']),
};

// Fallback: convert PokeAPI kebab-case to Showdown title-case ('fire-blast' → 'Fire Blast')
// Used when @smogon/calc doesn't have the move in its gen data (e.g. newer TMs)
function kebabToTitle(str) {
  return str.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Cache: `${pokemonKey}:${genNum}` → sorted array of Showdown-format move names, or null
const movePoolCache = {};

async function fetchMovePool(pokemonKey, genNum) {
  const cacheKey = `${pokemonKey}:${genNum}`;
  if (cacheKey in movePoolCache) return movePoolCache[cacheKey];

  try {
    const res = await fetch(`https://pokeapi.co/api/v2/pokemon/${pokemonKey}`);
    if (!res.ok) throw new Error('not found');
    const data = await res.json();
    const genObj        = getGen(genNum);
    const validGroups   = GEN_VERSION_GROUPS[genNum] || new Set();
    const seen = new Set();
    const pool = [];

    for (const m of (data.moves || [])) {
      // Only include moves learnable in the target generation
      const inGen = validGroups.size === 0 ||
        m.version_group_details.some(vg => validGroups.has(vg.version_group.name));
      if (!inGen) continue;

      // Prefer the Showdown-canonical name from @smogon/calc; fall back to title-case
      // conversion so TMs not yet in smogon's data are still included
      const entry    = genObj.moves.get(m.move.name);
      const moveName = entry?.name || kebabToTitle(m.move.name);

      if (!seen.has(moveName)) {
        seen.add(moveName);
        pool.push(moveName);
      }
    }
    pool.sort();
    movePoolCache[cacheKey] = pool.length > 0 ? pool : null;
  } catch {
    movePoolCache[cacheKey] = null;
  }
  return movePoolCache[cacheKey];
}

// Returns:
//   undefined  = no Pokémon selected (MoveSelect shows full fallback list)
//   null       = Pokémon selected, fetch in-flight
//   string[]   = loaded move pool
function usePokemonMoves(pokemonKey, gen) {
  const [moves, setMoves] = useState(undefined);
  useEffect(() => {
    if (!pokemonKey) { setMoves(undefined); return; }
    setMoves(null); // mark as loading
    let cancelled = false;
    fetchMovePool(pokemonKey, gen).then(pool => {
      if (!cancelled) setMoves(pool ?? []); // empty array if fetch fails (still "loaded")
    });
    return () => { cancelled = true; };
  }, [pokemonKey, gen]);
  return moves;
}

// ── State factories ───────────────────────────────────────────────────────────

let _defId = 0;
const nextDefId = () => ++_defId;

const DEFAULT_MOVE_OPTS = () => [
  { crit: false, hits: null },
  { crit: false, hits: null },
  { crit: false, hits: null },
  { crit: false, hits: null },
];

function defaultSide() {
  return {
    pokemonKey: '', showdownName: '', baseStats: null,
    types: [], knownAbilities: [], moves: ['', '', '', ''],
    moveOpts: DEFAULT_MOVE_OPTS(),
    level: 50, nature: 'Hardy', item: 'None', ability: '', teraType: '', status: '',
    evs:    { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
    ivs:    { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 },
    boosts: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
    isDynamaxed: false, isTerastallized: false, abilityOn: false, sprite: null,
  };
}

function defaultDefender() {
  return { ...defaultSide(), id: nextDefId(), move: '' };
}

const SIDE_DEFAULTS = {
  isReflect: false, isLightScreen: false, isAuroraVeil: false,
  isHelpingHand: false, isFriendGuard: false, isCharged: false,
  isTailwind: false,
  isTabletsOfRuin: false, isSwordOfRuin: false, isVesselOfRuin: false, isBeadsOfRuin: false,
  isSR: false, spikes: 0, steelsurge: false,
};

function defaultField() {
  return {
    weather: '', terrain: '', isGravity: false,
    attackerSide: { ...SIDE_DEFAULTS },
    defenderSide: { ...SIDE_DEFAULTS },
  };
}

function buildCondition(type, moveName, targetSide) {
  return {
    id: 0, type, koTier: 1, moveName,
    moveData: MOVE_DATA[moveName] || null,
    targetKey: targetSide.pokemonKey,
    targetShowdownName: targetSide.showdownName,
    targetBaseStats: targetSide.baseStats,
    targetConfig: {
      nature: targetSide.nature, item: targetSide.item, ability: targetSide.ability,
      evs: { ...targetSide.evs }, boosts: {},
      teraType: targetSide.isTerastallized ? targetSide.teraType : null,
      isDynamaxed: targetSide.isDynamaxed,
      weather: null, terrain: null,
      screens: { reflect: false, lightScreen: false, auroraVeil: false },
      isSpread: false,
    },
  };
}

function sideCalcKey(s) {
  if (!s.baseStats) return null;
  return [
    s.showdownName, s.level, s.nature, s.item, s.ability, s.abilityOn,
    s.status, s.isDynamaxed, s.isTerastallized, s.teraType,
    JSON.stringify(s.evs), JSON.stringify(s.ivs), JSON.stringify(s.boosts),
  ].join('|');
}

// ── Shared: extract damage range + KO text ────────────────────────────────────

function parseDamage(calcResult, defenderState) {
  if (!calcResult || calcResult === 'error') return null;
  let rolls = calcResult.damage;
  let minDmg, maxDmg;
  if (Array.isArray(rolls)) {
    if (Array.isArray(rolls[0])) {
      minDmg = rolls.reduce((s, r) => s + Math.min(...r), 0);
      maxDmg = rolls.reduce((s, r) => s + Math.max(...r), 0);
    } else {
      minDmg = Math.min(...rolls);
      maxDmg = Math.max(...rolls);
    }
  } else {
    minDmg = maxDmg = rolls ?? 0;
  }
  const defHP = defenderState.baseStats
    ? calcStat('hp', defenderState.baseStats.hp, defenderState.evs.hp, defenderState.ivs.hp, defenderState.level, defenderState.nature)
    : null;
  const pctMin = defHP ? ((minDmg / defHP) * 100).toFixed(1) : '?';
  const pctMax = defHP ? ((maxDmg / defHP) * 100).toFixed(1) : '?';
  const rollArr = Array.isArray(rolls) && !Array.isArray(rolls[0]) ? rolls : null;
  let koText = '', koClass = 'no-ko';
  if (defHP) {
    if (minDmg >= defHP) { koText = 'Guaranteed OHKO'; koClass = 'guaranteed'; }
    else if (rollArr && maxDmg >= defHP) {
      const count = rollArr.filter(r => r >= defHP).length;
      koText = `${count}/16 OHKO (${((count / 16) * 100).toFixed(0)}%)`; koClass = 'possible';
    } else if (minDmg * 2 >= defHP) { koText = 'Guaranteed 2HKO'; koClass = 'guaranteed'; }
    else if (rollArr && maxDmg * 2 >= defHP) {
      const count = rollArr.filter(r => r * 2 >= defHP).length;
      koText = `${count}/16 2HKO`; koClass = 'possible';
    } else if (minDmg * 3 >= defHP) { koText = 'Guaranteed 3HKO'; koClass = 'guaranteed'; }
    else if (rollArr && maxDmg * 3 >= defHP) {
      const count = rollArr.filter(r => r * 3 >= defHP).length;
      koText = `${count}/16 3HKO`; koClass = 'possible';
    } else if (minDmg * 4 >= defHP) { koText = 'Guaranteed 4HKO'; koClass = 'weak'; }
    else { koText = 'Does not threaten KO'; koClass = 'no-ko'; }
  }
  return { minDmg, maxDmg, defHP, pctMin, pctMax, koText, koClass };
}

// ── Showdown paste import ─────────────────────────────────────────────────────
// Parses a standard Showdown export set into a flat object ready to merge into side state.
const SD_STAT_MAP = { HP: 'hp', Atk: 'atk', Def: 'def', SpA: 'spa', SpD: 'spd', Spe: 'spe' };

function parseShowdownSet(text) {
  const lines = text.trim().split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;

  // Line 0: "Pokémon @ Item"  OR  just "Pokémon"
  const headerMatch = lines[0].match(/^(.+?)(?:\s+@\s+(.+))?$/);
  if (!headerMatch) return null;
  const rawName = headerMatch[1].trim();
  const item    = headerMatch[2]?.trim() || 'None';

  // pokemonKey = lowercase with hyphens (our internal format)
  const pokemonKey = rawName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  let ability = '', nature = 'Hardy', teraType = '';
  const evs = { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 };
  const ivs  = { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 };
  const moves = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('-')) {
      const mv = line.replace(/^-\s*/, '').replace(/\s*\(.*\)/, '').trim();
      if (mv) moves.push(mv);
    } else if (line.startsWith('Ability:')) {
      ability = line.replace('Ability:', '').trim();
    } else if (line.startsWith('Tera Type:')) {
      teraType = line.replace('Tera Type:', '').trim();
    } else if (line.startsWith('EVs:')) {
      line.replace('EVs:', '').split('/').forEach(part => {
        const m = part.trim().match(/^(\d+)\s+(\w+)$/);
        if (m && SD_STAT_MAP[m[2]]) evs[SD_STAT_MAP[m[2]]] = Math.min(252, +m[1]);
      });
    } else if (line.startsWith('IVs:')) {
      line.replace('IVs:', '').split('/').forEach(part => {
        const m = part.trim().match(/^(\d+)\s+(\w+)$/);
        if (m && SD_STAT_MAP[m[2]]) ivs[SD_STAT_MAP[m[2]]] = Math.min(31, +m[1]);
      });
    } else if (line.endsWith('Nature')) {
      const n = line.replace('Nature', '').trim();
      if (NATURES.includes(n)) nature = n;
    }
  }

  while (moves.length < 4) moves.push('');
  return { pokemonKey, item, ability, nature, teraType, evs, ivs, moves: moves.slice(0, 4) };
}

// ── Move property detection ────────────────────────────────────────────────────
// Returns alwaysCrit (willCrit in dex data), fixedHits (locked count or null),
// and isMultiHit (true when variable multi-hit, user can override hits).
function getMoveProps(moveName, genObj) {
  if (!moveName || !genObj) return { alwaysCrit: false, fixedHits: null, isMultiHit: false };
  try {
    const m = new Move(genObj, moveName);
    const alwaysCrit = m.isCrit;
    if (m.hits <= 1) return { alwaysCrit, fixedHits: null, isMultiHit: false };
    // If requesting 999 hits still returns the same count, hits are fixed (stored as
    // a number in the dex data, e.g. Surging Strikes = 3, Triple Axel = 3).
    const mTest = new Move(genObj, moveName, { hits: 999 });
    const fixedHits = mTest.hits === m.hits ? m.hits : null;
    return { alwaysCrit, fixedHits, isMultiHit: true };
  } catch { return { alwaysCrit: false, fixedHits: null, isMultiHit: false }; }
}

// Abilities that expose an "Active" toggle: their on/off state changes the calc.
const ABILITY_ON_ABILITIES = new Set(['Flash Fire', 'Stakeout', 'Plus', 'Minus', 'Slow Start']);

// ── Sub-components ────────────────────────────────────────────────────────────

// Compact nature chip shown on the card (not buried in a dropdown)
function NatureBadge({ nature }) {
  const nat = NATURE_TABLE[nature];
  if (!nat?.boost) return <span className="qc-nature-badge neutral">{nature}</span>;
  return (
    <span className="qc-nature-badge boosted">
      {nature}
      <span className="qc-nature-boost">+{SMOGON_TO_LABEL[nat.boost]}</span>
      <span className="qc-nature-reduce">−{SMOGON_TO_LABEL[nat.reduce]}</span>
    </span>
  );
}

function PokemonSearch({ selectedKey, onSelect, onClear, placeholder, types }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const ref = useRef(null);

  const handleChange = e => {
    const q = e.target.value;
    setQuery(q);
    if (q.length < 2) { setSuggestions([]); return; }
    const lower = q.toLowerCase().replace(/\s+/g, '-');
    const regular = ALL_POKEMON_NAMES.filter(n => {
      const resolved = resolveKey(n);
      return n.startsWith(lower) || n.includes(lower) || resolved.includes(lower);
    }).slice(0, 6).map(n => ({ name: n, isMega: false }));
    const megas = ALL_MEGA_KEYS.filter(n => n.includes(lower))
      .slice(0, 4).map(n => ({ name: n, isMega: true }));
    setSuggestions([...regular, ...megas].slice(0, 10));
    setFocusedIdx(-1);
  };

  const handleKey = e => {
    if (e.key === 'ArrowDown') setFocusedIdx(i => Math.min(i + 1, suggestions.length - 1));
    else if (e.key === 'ArrowUp') setFocusedIdx(i => Math.max(i - 1, 0));
    else if (e.key === 'Enter' && focusedIdx >= 0) pick(suggestions[focusedIdx].name);
    else if (e.key === 'Escape') setSuggestions([]);
  };

  const pick = name => { setSuggestions([]); setQuery(''); onSelect(name); };

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setSuggestions([]); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (selectedKey) {
    return (
      <div className="qc-selected-pill">
        <span className="qc-selected-name">{selectedKey.replace(/-/g, ' ')}</span>
        {types && types.length > 0 && (
          <span className="qc-type-chips">
            {types.map(t => (
              <span key={t} className="qc-type-chip" style={{ background: TYPE_COLORS[t] || '#a8a878' }}>{t}</span>
            ))}
          </span>
        )}
        <button className="qc-clear-btn" onClick={onClear} title="Clear">✕</button>
      </div>
    );
  }

  return (
    <div className="qc-search-wrap" ref={ref}>
      <input
        className="qc-search-input" type="text"
        placeholder={placeholder || 'Search any Pokémon…'}
        value={query} onChange={handleChange} onKeyDown={handleKey} autoComplete="off"
      />
      {suggestions.length > 0 && (
        <div className="qc-autocomplete">
          {suggestions.map(({ name, isMega }, i) => (
            <div key={name}
              className={`qc-autocomplete-item${i === focusedIdx ? ' focused' : ''}${isMega ? ' mega' : ''}`}
              onMouseDown={() => pick(name)}
            >
              {isMega
                ? name.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                : resolveKey(name).replace(/-/g, ' ')}
              {isMega && <span className="qc-mega-badge">MEGA</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const StatGrid = React.memo(function StatGrid({ baseStats, evs, ivs, boosts, nature, level, side, onEvStep, onEvInput, onIvInput, onBoostStep, highlightStat }) {
  const totalEvs = Object.values(evs).reduce((s, v) => s + v, 0);
  const evTotalClass = totalEvs > 510 ? ' over' : totalEvs > 500 ? ' near' : '';

  function getNatureClass(statKey) {
    const natInfo = NATURE_TABLE[nature];
    const smogKey = NATURE_STAT_MAP[statKey];
    if (!natInfo || !smogKey) return '';
    if (natInfo.boost === smogKey)  return ' stat-boosted';
    if (natInfo.reduce === smogKey) return ' stat-reduced';
    return '';
  }

  return (
    <div className="qc-stat-table-wrap">
      <table className="qc-stat-grid-table">
        <thead>
          <tr>
            <th>Stat</th>
            <th><span data-tip="Base stat value — fixed for each species and form">Base</span></th>
            <th><span data-tip="+1=×1.5, +2=×2, +3=×2.5, +4=×3, +5=×3.5, +6=×4 (negatives reduce)">Boost</span></th>
            <th><span data-tip="Effort Values: 4 EVs = +1 final stat. Max 252 per stat, 510 total">EVs</span></th>
            <th><span data-tip="Individual Values: 31 is perfect. Use 0 Atk IVs if running no physical moves">IVs</span></th>
            <th><span data-tip="Final calculated stat used in the damage formula">Final</span></th>
          </tr>
        </thead>
        <tbody>
          {STAT_KEYS.map(k => {
            const base = baseStats?.[k] ?? 0;
            const finalVal = base ? calcStat(k, base, evs[k], ivs[k], level, nature) : '—';
            const boost = boosts[k] || 0;
            return (
              <tr key={k} className={k === highlightStat ? 'qc-stat-relevant' : ''}>
                <td className={`qc-stat-name-cell${getNatureClass(k)}`}>{statLabel(k)}</td>
                <td className="qc-base-stat">
                  <div className="qc-base-stat-inner">
                    <span>{base || '—'}</span>
                    {base > 0 && (
                      <div className="qc-stat-bar-track">
                        <div className="qc-stat-bar" style={{ width: `${Math.min(100, (base / 255) * 100)}%`, background: STAT_BAR_COLORS[k] }} />
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="qc-boost-cell">
                    <button className="qc-boost-btn" onClick={() => onBoostStep(side, k, -1)} disabled={boost <= -6}>−</button>
                    <span className={`qc-boost-val${boost > 0 ? ' positive' : boost < 0 ? ' negative' : ''}`}>
                      {boost > 0 ? `+${boost}` : boost === 0 ? '—' : boost}
                    </span>
                    <button className="qc-boost-btn" onClick={() => onBoostStep(side, k, +1)} disabled={boost >= 6}>+</button>
                  </div>
                </td>
                <td>
                  <div className="qc-ev-group">
                    <button className="qc-ev-zero-btn" onClick={() => onEvInput(side, k, 0)} disabled={evs[k] <= 0} title="Set to 0">0</button>
                    <button className="qc-ev-step-btn" onClick={() => onEvStep(side, k, -1)} disabled={evs[k] <= 0}>−</button>
                    <input className="qc-ev-input" type="number" min={0} max={252} step={4}
                      value={evs[k]} onChange={e => onEvInput(side, k, e.target.value)} />
                    <button className="qc-ev-step-btn" onClick={() => onEvStep(side, k, +1)} disabled={evs[k] >= 252}>+</button>
                    <button className="qc-ev-max-btn" onClick={() => onEvInput(side, k, 252)} disabled={evs[k] >= 252} title="Set to 252">M</button>
                  </div>
                </td>
                <td>
                  <input className="qc-iv-input" type="number" min={0} max={31}
                    value={ivs[k]} onChange={e => onIvInput(side, k, e.target.value)} />
                </td>
                <td className="qc-final-stat">{finalVal}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={`qc-ev-total${evTotalClass}`}>EVs: {totalEvs} / 510</div>
    </div>
  );
});


// ── Compact damage cell used in the results grid ─────────────────────────────

function DmgCell({ result, defenderState }) {
  if (!result) return <span className="qc-dmg-empty">—</span>;
  if (result === 'error') return <span className="qc-dmg-err">err</span>;
  const d = parseDamage(result, defenderState);
  if (!d) return <span className="qc-dmg-empty">—</span>;
  let koLabel = '';
  if (d.koClass !== 'no-ko') {
    koLabel = d.koText.startsWith('Guaranteed ') ? d.koText.slice(11) : d.koText.replace(/\s*\(\d+%\)/, '');
  }
  // Full Showdown-style description shown on hover
  let tip = null;
  try { tip = result.fullDesc?.() ?? null; } catch { /* ignore */ }
  return (
    <div className="qc-dmg-result" data-tip={tip} data-tip-wrap>
      <span className="qc-dmg-pct">{d.pctMin}–{d.pctMax}%</span>
      {koLabel && <span className={`qc-dmg-ko-badge ${d.koClass}`}>{koLabel}</span>}
    </div>
  );
}

// ── Results panel (shown above the card grid) ─────────────────────────────────

function DamageResults({ attacker, defenders, attackerMove, resultsOut, resultsIn }) {
  const atkMoves = attacker.moves || ['', '', '', ''];
  const activeDefenders = defenders.filter(d => d.baseStats);
  const filledAtkMoves  = atkMoves.filter(Boolean);
  const defsWithMoves   = activeDefenders.filter(d => (d.moves || []).some(Boolean));

  if (!attacker.baseStats || activeDefenders.length === 0) return null;
  if (filledAtkMoves.length === 0 && defsWithMoves.length === 0) return null;

  const atkName     = attacker.pokemonKey ? attacker.pokemonKey.replace(/-/g, ' ') : 'Your Pokémon';
  const nDefs       = activeDefenders.length;
  const outGridCols = `minmax(90px, 1.4fr) ${Array(nDefs).fill('1fr').join(' ')}`;

  // For each defender, find which move index deals the highest max damage %
  const bestMovePerDef = {};
  activeDefenders.forEach(def => {
    let bestMi = null, bestPct = -Infinity;
    atkMoves.forEach((mv, mi) => {
      if (!mv) return;
      const result = resultsOut[def.id]?.[mi];
      if (!result || result === 'error') return;
      const d = parseDamage(result, def);
      if (!d) return;
      const pct = parseFloat(d.pctMax);
      if (pct > bestPct) { bestPct = pct; bestMi = mi; }
    });
    if (bestMi !== null) bestMovePerDef[def.id] = bestMi;
  });

  return (
    <div className="qc-damage-results">
      {/* ── Outgoing ── */}
      {filledAtkMoves.length > 0 && (
        <div className="qc-dmg-section">
          <div className="qc-dmg-section-header outgoing">
            <span className="qc-dmg-arrow">▶</span>
            <span className="qc-dmg-section-title">
              {atkName} <span className="qc-dmg-vs">vs. opponents</span>
            </span>
          </div>
          <div className="qc-dmg-grid-scroll">
            <div className="qc-dmg-grid" style={{ gridTemplateColumns: outGridCols }}>
              <div className="qc-dmg-th" />
              {activeDefenders.map((def, i) => (
                <div key={def.id} className="qc-dmg-th">
                  {def.pokemonKey ? def.pokemonKey.replace(/-/g, ' ') : `Target ${i + 1}`}
                </div>
              ))}
              {atkMoves.map((mv, mi) => {
                if (!mv) return null;
                // Prefer type from the calc result (covers all moves); fall back to MOVE_DATA
                const anyResult = activeDefenders.map(d => resultsOut[d.id]?.[mi]).find(r => r && r !== 'error');
                const moveType = anyResult?.move?.type || MOVE_DATA[mv]?.type;
                return (
                  <React.Fragment key={mi}>
                    <div className={`qc-dmg-move${mv === attackerMove ? ' active' : ''}`}>
                      {moveType && (
                        <span className="qc-dmg-type-chip" style={{ background: TYPE_COLORS[moveType] || '#888' }}>
                          {moveType}
                        </span>
                      )}
                      {mv}
                    </div>
                    {activeDefenders.map(def => (
                      <div key={def.id} className={`qc-dmg-td${bestMovePerDef[def.id] === mi ? ' best' : ''}`}>
                        <DmgCell result={resultsOut[def.id]?.[mi]} defenderState={def} />
                      </div>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Incoming ── */}
      {defsWithMoves.length > 0 && (
        <div className="qc-dmg-section">
          <div className="qc-dmg-section-header incoming">
            <span className="qc-dmg-arrow">◀</span>
            <span className="qc-dmg-section-title">
              opponents <span className="qc-dmg-vs">vs. {atkName}</span>
            </span>
          </div>
          <div className="qc-dmg-incoming-wrap">
            {defsWithMoves.map((def, di) => {
              const defMoves = def.moves || ['', '', '', ''];
              const defName  = def.pokemonKey ? def.pokemonKey.replace(/-/g, ' ') : `Target ${di + 1}`;
              return (
                <div key={def.id} className="qc-dmg-incoming-def">
                  <div className="qc-dmg-def-name">{defName}</div>
                  <div className="qc-dmg-def-moves">
                    {defMoves.map((mv, mi) => {
                      if (!mv) return null;
                      return (
                        <div key={mi} className={`qc-dmg-inc-row${mv === def.move ? ' active' : ''}`}>
                          <span className="qc-dmg-inc-move">{mv}</span>
                          <DmgCell result={resultsIn[def.id]?.[mi]} defenderState={attacker} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldPanel({ gen, regulation, fieldConfig, level, onGenChange, onRegulationChange, onFieldUpdate, onLevelChange, onAttackerSideUpdate, onDefenderSideUpdate }) {
  return (
    <div className="qc-field-panel">
      <div className="qc-field-row">
        <span className="qc-field-row-label">Gen</span>
        {[1,2,3,4,5,6,7,8,9].map(g => (
          <button key={g} className={`qc-gen-btn${gen === g ? ' active' : ''}`} onClick={() => onGenChange(g)}>{g}</button>
        ))}
        <span className="qc-field-row-label" style={{ marginLeft: '10px' }}>Lv</span>
        <input
          type="number" min={1} max={100} value={level}
          className="qc-level-input"
          onChange={e => onLevelChange(e.target.value)}
        />
      </div>

      {/* ── Regulation skeleton ── when REGULATION_POKEMON is populated this will
          filter search suggestions and surface common sets for that format.     */}
      <div className="qc-field-row">
        <span className="qc-field-row-label">Format</span>
        {REGULATIONS.map(r => (
          <button key={r.key}
            className={`qc-toggle-pill${regulation === r.key ? ' active' : ''}`}
            onClick={() => onRegulationChange(regulation === r.key ? null : r.key)}
          >
            {r.label}
          </button>
        ))}
        {regulation && (
          <span className="qc-reg-hint">
            Common sets coming soon
          </span>
        )}
      </div>

      <div className="qc-field-row">
        <span className="qc-field-row-label">Weather</span>
        {[
          { val: 'Sun',             label: 'Sun',      tip: 'Fire ×1.5, Water ×0.5. Powers up Solar Beam, Synthesis.' },
          { val: 'Rain',            label: 'Rain',     tip: 'Water ×1.5, Fire ×0.5. Thunder 100% accuracy.' },
          { val: 'Sand',            label: 'Sand',     tip: '1/16 HP/turn to non-Rock/Ground/Steel. Rock SpDef ×1.5.' },
          { val: 'Snow',            label: 'Snow',     tip: 'Ice Defense ×1.5. Gen 9 replacement for Hail.' },
          { val: 'Hail',            label: 'Hail',     tip: '1/16 HP/turn to non-Ice types. Blizzard 100% accuracy.' },
          { val: 'Harsh Sunshine',  label: 'H.Sun',    tip: 'Primal Sun — Fire ×1.5, Water moves fail completely.' },
          { val: 'Heavy Rain',      label: 'H.Rain',   tip: 'Primal Rain — Water ×1.5, Fire moves fail completely.' },
          { val: 'Strong Winds',    label: 'Winds',    tip: 'Delta Stream — Flying types take neutral from their weaknesses.' },
        ].filter(w => WEATHER_BTNS.includes(w.val)).map(({ val, label, tip }) => (
          <button key={val} data-tip={tip}
            className={`qc-toggle-pill${fieldConfig.weather === val ? ' active' : ''}`}
            onClick={() => onFieldUpdate({ weather: fieldConfig.weather === val ? '' : val })}>{label}</button>
        ))}
      </div>

      <div className="qc-field-row">
        <span className="qc-field-row-label">Terrain</span>
        {[
          { val: 'Electric', tip: 'Boosts Electric moves ×1.3 for grounded Pokémon. Prevents sleep.' },
          { val: 'Grassy',   tip: 'Boosts Grass moves ×1.3. Restores 1/16 HP per turn. Halves Earthquake/Bulldoze damage.' },
          { val: 'Misty',    tip: 'Halves Dragon-type damage. Prevents status conditions on grounded Pokémon.' },
          { val: 'Psychic',  tip: 'Boosts Psychic moves ×1.3. Blocks priority moves against grounded Pokémon.' },
        ].map(({ val, tip }) => (
          <button key={val} data-tip={tip}
            className={`qc-toggle-pill${fieldConfig.terrain === val ? ' active' : ''}`}
            onClick={() => onFieldUpdate({ terrain: fieldConfig.terrain === val ? '' : val })}>{val}</button>
        ))}
        <button data-tip="Eliminates all immunity to Ground-type moves. Reduces jump/fly mechanics. Lowers all Pokémon to the ground."
          className={`qc-toggle-pill${fieldConfig.isGravity ? ' active' : ''}`}
          onClick={() => onFieldUpdate({ isGravity: !fieldConfig.isGravity })}>Gravity</button>
      </div>

      <div className="qc-field-row">
        <span className="qc-field-row-label">Sides</span>
        {SIDED_EFFECTS.map(({ key, label, tip }) => {
          const yours = fieldConfig.attackerSide[key];
          const theirs = fieldConfig.defenderSide[key];
          return (
            <div key={key} className={`qc-sided-pill${yours || theirs ? ' has-active' : ''}`}>
              <button
                className={`qc-side-arrow your-side${yours ? ' active' : ''}`}
                onClick={() => onAttackerSideUpdate({ [key]: !yours })}
                title="Your side"
              >◄</button>
              <span className="qc-sided-label" data-tip={tip}>{label}</span>
              <button
                className={`qc-side-arrow opp-side${theirs ? ' active' : ''}`}
                onClick={() => onDefenderSideUpdate({ [key]: !theirs })}
                title="Opp side"
              >►</button>
            </div>
          );
        })}
      </div>

      {/* ── Hazards ── Stealth Rock, Spikes (0-3), Steelsurge per side ── */}
      <div className="qc-field-row">
        <span className="qc-field-row-label">Hazards</span>

        {/* Stealth Rock */}
        {(() => {
          const ySR = fieldConfig.attackerSide.isSR;
          const tSR = fieldConfig.defenderSide.isSR;
          return (
            <div className={`qc-sided-pill${ySR || tSR ? ' has-active' : ''}`}>
              <button className={`qc-side-arrow your-side${ySR ? ' active' : ''}`}
                onClick={() => onAttackerSideUpdate({ isSR: !ySR })} title="Your side">◄</button>
              <span className="qc-sided-label" data-tip="Damages Pokémon on switch-in based on Rock-type effectiveness (6.25%–50% HP)">SR</span>
              <button className={`qc-side-arrow opp-side${tSR ? ' active' : ''}`}
                onClick={() => onDefenderSideUpdate({ isSR: !tSR })} title="Opp side">►</button>
            </div>
          );
        })()}

        {/* Spikes — cycling 0→1→2→3→0 */}
        {(() => {
          const ySpk = fieldConfig.attackerSide.spikes || 0;
          const tSpk = fieldConfig.defenderSide.spikes || 0;
          return (
            <div className={`qc-sided-pill${ySpk > 0 || tSpk > 0 ? ' has-active' : ''}`}>
              <button className={`qc-side-arrow your-side${ySpk > 0 ? ' active' : ''}`}
                onClick={() => onAttackerSideUpdate({ spikes: (ySpk + 1) % 4 })} title="Your side spikes (click to cycle layers)">
                {ySpk > 0 ? `◄×${ySpk}` : '◄'}
              </button>
              <span className="qc-sided-label" data-tip="Spikes: 1–3 layers deal 12.5/16.7/25% damage on switch-in (non-Flying/Levitate)">Spikes</span>
              <button className={`qc-side-arrow opp-side${tSpk > 0 ? ' active' : ''}`}
                onClick={() => onDefenderSideUpdate({ spikes: (tSpk + 1) % 4 })} title="Opp side spikes (click to cycle layers)">
                {tSpk > 0 ? `×${tSpk}►` : '►'}
              </button>
            </div>
          );
        })()}

        {/* Steelsurge */}
        {(() => {
          const ySS = fieldConfig.attackerSide.steelsurge;
          const tSS = fieldConfig.defenderSide.steelsurge;
          return (
            <div className={`qc-sided-pill${ySS || tSS ? ' has-active' : ''}`}>
              <button className={`qc-side-arrow your-side${ySS ? ' active' : ''}`}
                onClick={() => onAttackerSideUpdate({ steelsurge: !ySS })} title="Your side">◄</button>
              <span className="qc-sided-label" data-tip="Steelsurge: Steel-type spikes damage based on Steel-type effectiveness on switch-in">Steel</span>
              <button className={`qc-side-arrow opp-side${tSS ? ' active' : ''}`}
                onClick={() => onDefenderSideUpdate({ steelsurge: !tSS })} title="Opp side">►</button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ── EV Tools Panel ────────────────────────────────────────────────────────────

function EvToolsPanel({ attacker, defenders, gen, onApplyEvs }) {
  const [tab, setTab] = useState('survive');
  const [surviveChecks, setSurviveChecks] = useState({});
  const [koChecks, setKoChecks]           = useState({});
  const [feedback, setFeedback]           = useState(null);
  const fbTimer = useRef(null);

  // Reset checks when the defender lineup changes
  const defKey = defenders.map(d => d.id).join(',');
  useEffect(() => {
    setSurviveChecks({});
    setKoChecks({});
  }, [defKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function showFeedback(msg) {
    clearTimeout(fbTimer.current);
    setFeedback(msg);
    fbTimer.current = setTimeout(() => setFeedback(null), 5000);
  }

  // ── Survive ──────────────────────────────────────────────────────────────
  function handleSurviveSubmit() {
    const conditions = [];
    defenders.forEach(def => {
      (def.moves || []).forEach((move, mi) => {
        const key = `${def.id}_${mi}`;
        if (!surviveChecks[key] || !move || STATUS_MOVES.has(move)) return;
        conditions.push({ ...buildCondition('survive', move, def), id: conditions.length });
      });
    });
    if (!conditions.length) { showFeedback('Select at least one move to survive.'); return; }

    const myConfig = {
      nature: attacker.nature, item: attacker.item, ability: attacker.ability,
      evs: { ...attacker.evs }, boosts: {},
      level: attacker.level,
      teraType: attacker.isTerastallized ? attacker.teraType : null,
      isDynamaxed: attacker.isDynamaxed,
      weather: null, terrain: null,
      screens: { reflect: false, lightScreen: false, auroraVeil: false },
      isSpread: false,
    };

    try {
      const result = generateSpreads(gen, attacker, myConfig, conditions);
      if (!result) { showFeedback('Optimizer failed.'); return; }

      const newEvs = { ...attacker.evs };
      // Compute the maximum of each minimum across all conditions, starting from 0
      let minHP = 0, minDefEvs = 0, minDefStat = null;
      const suggestedNatures = [];
      result.conditionSummary.forEach(({ status, minEvs }) => {
        if (!minEvs || status !== 'met') return;
        minHP = Math.max(minHP, minEvs.hp ?? 0);
        if (minEvs.defStat) {
          minDefStat = minEvs.defStat;
          minDefEvs = Math.max(minDefEvs, minEvs.defEvs ?? 0);
        }
        if (minEvs.nature) suggestedNatures.push(minEvs.nature);
      });
      newEvs.hp = minHP;
      if (minDefStat) newEvs[minDefStat] = minDefEvs;
      // Apply nature only if all met conditions agree on the same one
      const uniqueNatures = [...new Set(suggestedNatures)];
      const patch = { evs: newEvs };
      if (uniqueNatures.length === 1 && uniqueNatures[0] !== attacker.nature) {
        patch.nature = uniqueNatures[0];
      }
      const met   = result.conditionSummary.filter(b => b.status === 'met').length;
      const total = result.conditionSummary.length;
      onApplyEvs(patch);
      showFeedback(`Survives ${met}/${total} selected moves — EVs applied${patch.nature ? ` (nature → ${patch.nature})` : ''}.`);
    } catch {
      showFeedback('Calculation error.');
    }
  }

  // ── OHKO ─────────────────────────────────────────────────────────────────
  function handleKoSubmit() {
    const selectedDefs = defenders.filter(d => koChecks[d.id] && d.pokemonKey);
    if (!selectedDefs.length) { showFeedback('Select at least one target to OHKO.'); return; }

    // For each selected defender, pick the first damaging attacker move
    const conditions = [];
    selectedDefs.forEach(def => {
      const moveName = (attacker.moves || []).find(m => m && !STATUS_MOVES.has(m));
      if (!moveName) return;
      conditions.push({ ...buildCondition('ko', moveName, def), id: conditions.length });
    });
    if (!conditions.length) { showFeedback('Add a damaging move to your Pokémon first.'); return; }

    const myConfig = {
      nature: attacker.nature, item: attacker.item, ability: attacker.ability,
      evs: { ...attacker.evs }, boosts: {},
      level: attacker.level,
      teraType: attacker.isTerastallized ? attacker.teraType : null,
      isDynamaxed: attacker.isDynamaxed,
      weather: null, terrain: null,
      screens: { reflect: false, lightScreen: false, auroraVeil: false },
      isSpread: false,
    };

    try {
      const result = generateSpreads(gen, attacker, myConfig, conditions);
      if (!result) { showFeedback('Optimizer failed.'); return; }

      const newEvs = { ...attacker.evs };
      // Compute the maximum of each minimum across all conditions, starting from 0
      let minAttEvs = 0, minAttStat = null;
      const suggestedNatures = [];
      result.conditionSummary.forEach(({ status, minEvs }) => {
        if (!minEvs || status !== 'met') return;
        if (minEvs.stat) {
          minAttStat = minEvs.stat;
          minAttEvs = Math.max(minAttEvs, minEvs.evs ?? 0);
        }
        if (minEvs.nature) suggestedNatures.push(minEvs.nature);
      });
      if (minAttStat) newEvs[minAttStat] = minAttEvs;
      // Apply nature only if all met conditions agree on the same one
      const uniqueNatures = [...new Set(suggestedNatures)];
      const patch = { evs: newEvs };
      if (uniqueNatures.length === 1 && uniqueNatures[0] !== attacker.nature) {
        patch.nature = uniqueNatures[0];
      }
      const met   = result.conditionSummary.filter(b => b.status === 'met').length;
      const total = result.conditionSummary.length;
      onApplyEvs(patch);
      showFeedback(`KOs ${met}/${total} targets — EVs applied${patch.nature ? ` (nature → ${patch.nature})` : ''}.`);
    } catch {
      showFeedback('Calculation error.');
    }
  }

  // ── Speed rows ────────────────────────────────────────────────────────────
  const speedRows = useMemo(() => {
    if (!attacker.baseStats) return [];
    return defenders
      .filter(d => d.pokemonKey && d.baseStats)
      .map(def => {
        const defSpd = calcStat('spe', def.baseStats.spe, def.evs.spe, def.ivs.spe, def.level, def.nature);
        const target = defSpd + 1;
        let neededEv = null;
        for (let ev = 0; ev <= 252; ev += 4) {
          const mySpd = calcStat('spe', attacker.baseStats.spe, ev, attacker.ivs.spe, attacker.level, attacker.nature);
          if (mySpd >= target) { neededEv = ev; break; }
        }
        return { def, defSpd, neededEv };
      })
      .sort((a, b) => a.defSpd - b.defSpd);
  }, [attacker, defenders]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeDefs = defenders.filter(d => d.pokemonKey);

  return (
    <div className="qc-ev-tools">
      <div className="qc-ev-tab-bar">
        {[
          { key: 'survive', label: 'Survive' },
          { key: 'ohko',    label: 'OHKO'    },
          { key: 'speed',   label: 'Speed'   },
        ].map(({ key, label }) => (
          <button key={key}
            className={`qc-ev-tab${tab === key ? ' active' : ''}`}
            onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'survive' && (
        <div className="qc-ev-section">
          {activeDefs.length === 0 && (
            <p className="qc-ev-hint">Add opponents and set their moves to see options.</p>
          )}
          {activeDefs.map(def => {
            const damagingMoves = (def.moves || []).filter(m => m && !STATUS_MOVES.has(m));
            if (!damagingMoves.length) return null;
            return (
              <div key={def.id} className="qc-ev-def-group">
                <span className="qc-ev-def-label">{def.showdownName || def.pokemonKey}</span>
                {(def.moves || []).map((move, mi) => {
                  if (!move || STATUS_MOVES.has(move)) return null;
                  const key = `${def.id}_${mi}`;
                  return (
                    <label key={key} className="qc-ev-check-row">
                      <input type="checkbox" checked={!!surviveChecks[key]}
                        onChange={e => setSurviveChecks(prev => ({ ...prev, [key]: e.target.checked }))} />
                      {move}
                    </label>
                  );
                })}
              </div>
            );
          })}
          <button className="qc-ev-submit"
            disabled={!attacker.pokemonKey || activeDefs.length === 0}
            onClick={handleSurviveSubmit}>
            Find Min Survive EVs
          </button>
        </div>
      )}

      {tab === 'ohko' && (
        <div className="qc-ev-section">
          {activeDefs.length === 0 && (
            <p className="qc-ev-hint">Add opponents to select OHKO targets.</p>
          )}
          {activeDefs.map(def => (
            <label key={def.id} className="qc-ev-check-row">
              <input type="checkbox" checked={!!koChecks[def.id]}
                onChange={e => setKoChecks(prev => ({ ...prev, [def.id]: e.target.checked }))} />
              {def.showdownName || def.pokemonKey}
            </label>
          ))}
          <button className="qc-ev-submit"
            disabled={!attacker.pokemonKey || activeDefs.length === 0}
            onClick={handleKoSubmit}>
            Find Min OHKO EVs
          </button>
        </div>
      )}

      {tab === 'speed' && (
        <div className="qc-ev-section">
          {speedRows.length === 0 && (
            <p className="qc-ev-hint">Add opponents to see speed tiers.</p>
          )}
          {speedRows.length > 0 && (
            <table className="qc-ev-speed-table">
              <thead>
                <tr>
                  <th>Pokémon</th>
                  <th>Their Spd</th>
                  <th>EVs to outspeed</th>
                </tr>
              </thead>
              <tbody>
                {speedRows.map(({ def, defSpd, neededEv }) => (
                  <tr key={def.id}
                    className={neededEv == null ? 'qc-ev-impossible' : 'qc-ev-clickable'}
                    title={neededEv != null ? `Click to apply ${neededEv} Speed EVs` : undefined}
                    onClick={() => {
                      if (neededEv == null) return;
                      onApplyEvs({ evs: { ...attacker.evs, spe: neededEv } });
                      showFeedback(`Applied ${neededEv} Speed EVs to outspeed ${def.showdownName || def.pokemonKey}.`);
                    }}>
                    <td>{def.showdownName || def.pokemonKey}</td>
                    <td>{defSpd}</td>
                    <td>{neededEv == null ? 'Cannot outspeed' : `${neededEv} EVs`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {feedback && (
        <div className="qc-ev-feedback" onClick={() => setFeedback(null)}>{feedback}</div>
      )}
    </div>
  );
}

// ── Attacker Panel ────────────────────────────────────────────────────────────

function AttackerPanel({
  attacker, gen, attackerMove, defenders,
  onSelect, onClear, onUpdate, onEvStep, onEvInput, onIvInput, onBoostStep,
  onMoveChange, onApplyEvs, onImport,
}) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const movePool = usePokemonMoves(attacker.pokemonKey, gen);
  const genObj   = useMemo(() => getGen(gen), [gen]);
  const preset = attacker.pokemonKey ? COMMON_SETS[attacker.pokemonKey] : null;
  const moveCategory = MOVE_DATA[attackerMove]?.category;
  const attackerHighlight = moveCategory === 'Physical' ? 'atk' : moveCategory === 'Special' ? 'spa' : null;

  const handleLoadSet = () => {
    if (!preset) return;
    onUpdate({
      nature:  preset.nature  || attacker.nature,
      item:    preset.item    || attacker.item,
      ability: preset.ability || attacker.ability,
      evs:     preset.evs ? { ...preset.evs } : attacker.evs,
    });
  };

  const handlePaste = text => {
    const parsed = parseShowdownSet(text);
    if (parsed) { onImport(parsed); setPasteText(''); setPasteOpen(false); }
  };

  const opts  = attacker.moveOpts || DEFAULT_MOVE_OPTS();
  const moves = attacker.moves    || ['', '', '', ''];

  const updateMoveOpt = (i, patch) => {
    const next = opts.map((o, idx) => idx === i ? { ...o, ...patch } : o);
    onUpdate({ moveOpts: next });
  };

  return (
    <div className="qc-attacker-panel" style={attacker.types?.[0] ? { '--panel-accent': TYPE_COLORS[attacker.types[0]] } : {}}>
      <div className="qc-panel-label attacker">Your Pokémon</div>

      {/* Row 1: sprite inline + search */}
      <div className="qc-panel-header-row">
        {attacker.sprite && (
          <img className="qc-panel-sprite-inline" src={attacker.sprite} alt={attacker.showdownName} />
        )}
        <div className="qc-panel-header-info">
          <PokemonSearch selectedKey={attacker.pokemonKey} onSelect={onSelect} onClear={onClear} types={attacker.types} />
          {attacker.baseStats && <NatureBadge nature={attacker.nature} />}
        </div>
      </div>

      <div className="qc-panel-toolbar">
        {preset && (
          <button className="qc-quick-set-btn" onClick={handleLoadSet}
            data-tip="Loads a common competitive spread for this Pokémon (nature, item, EVs)">
            Quick Set — {preset.nature} · {preset.item}
          </button>
        )}
        <button className="qc-paste-btn" onClick={() => setPasteOpen(o => !o)}
          data-tip="Paste a Showdown export set to auto-fill all fields">
          {pasteOpen ? '✕ Close' : '📋 Import'}
        </button>
      </div>

      {pasteOpen && (
        <div className="qc-paste-wrap">
          <textarea className="qc-paste-area"
            value={pasteText}
            placeholder={"Paste Showdown set here:\n\nFlutter Mane @ Choice Specs\nAbility: Protosynthesis\nEVs: 252 SpA / 4 SpD / 252 Spe\nTimid Nature\n- Moonblast\n- Shadow Ball\n..."}
            onChange={e => setPasteText(e.target.value)}
            onPaste={e => {
              e.preventDefault();
              const text = e.clipboardData.getData('text');
              setPasteText(text);
              handlePaste(text);
            }}
          />
          {pasteText && (
            <button className="qc-paste-apply-btn" onClick={() => handlePaste(pasteText)}>Apply</button>
          )}
        </div>
      )}

      {attacker.baseStats && (
        <>
          {/* Row 2: 4 move slots with per-move crit/hits opts */}
          <div className="qc-move-slots">
            {moves.map((mv, i) => {
              const isActive = mv !== '' && mv === attackerMove;
              const currentNotInPool = mv && Array.isArray(movePool) && !movePool.includes(mv);
              const opt = opts[i] || { crit: false, hits: null };
              const { alwaysCrit, fixedHits, isMultiHit } = mv ? getMoveProps(mv, genObj) : { alwaysCrit: false, fixedHits: null, isMultiHit: false };
              return (
                <div key={i} className={`qc-move-slot-cell${isActive ? ' active' : ''}`}>
                  <select
                    className="qc-move-slot-select"
                    value={mv}
                    onChange={e => {
                      const newMoves = [...moves];
                      newMoves[i] = e.target.value;
                      onUpdate({ moves: newMoves });
                      if (e.target.value) {
                        onMoveChange(e.target.value);
                      } else if (mv === attackerMove) {
                        onMoveChange('');
                      }
                    }}
                  >
                    {!!attacker.pokemonKey && !Array.isArray(movePool) ? (
                      <option value="">Loading…</option>
                    ) : (
                      <option value="">{`Move ${i + 1}`}</option>
                    )}
                    {currentNotInPool && <option value={mv}>{mv}</option>}
                    {Array.isArray(movePool) ? (
                      movePool.map(m => <option key={m} value={m}>{m}</option>)
                    ) : (
                      <optgroup label="All Competitive Moves">
                        {ALL_MOVE_NAMES.map(m => <option key={m} value={m}>{m}</option>)}
                      </optgroup>
                    )}
                  </select>
                  {mv && (
                    <div className="qc-move-opts">
                      <button
                        className={`qc-crit-btn${alwaysCrit ? ' locked' : opt.crit ? ' active' : ''}`}
                        onClick={alwaysCrit ? undefined : () => updateMoveOpt(i, { crit: !opt.crit })}
                        title={alwaysCrit ? 'Always crits — inherent to this move' : 'Force critical hit — ignores defensive boosts, ×1.5 damage'}
                        disabled={alwaysCrit}
                      >★ Crit</button>
                      {fixedHits !== null && (
                        <span className="qc-fixed-hits-badge" title="Always hits this many times">×{fixedHits}</span>
                      )}
                      {fixedHits === null && isMultiHit && (
                        <select className="qc-hits-sel"
                          value={opt.hits || ''}
                          onChange={e => updateMoveOpt(i, { hits: e.target.value ? Number(e.target.value) : null })}
                          title="Override number of hits for this multi-hit move">
                          <option value="">×avg</option>
                          <option value="2">×2</option>
                          <option value="3">×3</option>
                          <option value="4">×4</option>
                          <option value="5">×5</option>
                        </select>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Row 3a: Nature / Item / Ability */}
          <div className="qc-config-inline">
            <div className="qc-field">
              <label data-tip="Boosts one stat by 10% and lowers another by 10%. Neutral natures have no effect.">Nature</label>
              <select value={attacker.nature} onChange={e => onUpdate({ nature: e.target.value })}>
                {NATURES.map(n => {
                  const nat = NATURE_TABLE[n];
                  const label = nat?.boost
                    ? `${n} (+${SMOGON_TO_LABEL[nat.boost]}/-${SMOGON_TO_LABEL[nat.reduce]})`
                    : `${n} (neutral)`;
                  return <option key={n} value={n}>{label}</option>;
                })}
              </select>
            </div>
            <div className="qc-field">
              <label>Item</label>
              {isMegaKey(attacker.pokemonKey) ? (
                <div className="qc-mega-stone-locked" title="Mega Pokémon must hold their Mega Stone">
                  <span className="qc-mega-stone-name">{attacker.item}</span>
                  <span className="qc-mega-stone-lock">🔒 Mega Stone</span>
                </div>
              ) : (
                <select value={attacker.item} onChange={e => onUpdate({ item: e.target.value })}>
                  {Object.entries(ITEM_GROUPS).map(([group, items]) => (
                    <optgroup key={group} label={group}>
                      {items.map(i => <option key={i} value={i}>{i}</option>)}
                    </optgroup>
                  ))}
                </select>
              )}
            </div>
            <div className="qc-field">
              <label data-tip="Some abilities affect damage calculations (e.g. Adaptability, Levitate, Flash Fire)">Ability</label>
              <input type="text" value={attacker.ability} placeholder="e.g. Adaptability"
                list="attacker-ability-list"
                onChange={e => onUpdate({ ability: e.target.value })} />
              {attacker.knownAbilities?.length > 0 && (
                <datalist id="attacker-ability-list">
                  {attacker.knownAbilities.map(a => <option key={a} value={a} />)}
                </datalist>
              )}
              {ABILITY_ON_ABILITIES.has(attacker.ability) && (
                <label className="qc-ability-on-check" title="Toggle whether this ability's conditional effect is active">
                  <input type="checkbox" checked={attacker.abilityOn}
                    onChange={e => onUpdate({ abilityOn: e.target.checked })} />
                  Active
                </label>
              )}
            </div>
          </div>

          {/* Row 3b: Tera / Status / flags */}
          <div className="qc-config-inline qc-config-secondary">
            <div className="qc-field">
              <label data-tip="Terastallizing changes the Pokémon's type. It gains STAB on its Tera type's moves.">Tera Type</label>
              <select value={attacker.teraType} onChange={e => onUpdate({ teraType: e.target.value })}>
                <option value="">None</option>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="qc-field">
              <label>Status</label>
              <select value={attacker.status} onChange={e => onUpdate({ status: e.target.value })}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'None'}</option>)}
              </select>
            </div>
            {gen === 8 && (
              <label className="qc-flag-label" data-tip="Doubles max HP and transforms moves into Max Moves (Gen 8 only)"
                style={{ alignSelf: 'flex-end', marginBottom: '3px' }}>
                <input type="checkbox" checked={attacker.isDynamaxed}
                  onChange={e => onUpdate({ isDynamaxed: e.target.checked })} />
                Dynamaxed
              </label>
            )}
            {gen === 9 && attacker.teraType && (
              <label className="qc-flag-label" data-tip="Activates Terastallize: changes type to the selected Tera type for this calc"
                style={{ alignSelf: 'flex-end', marginBottom: '3px' }}>
                <input type="checkbox" checked={attacker.isTerastallized}
                  onChange={e => onUpdate({ isTerastallized: e.target.checked })} />
                Tera'd
              </label>
            )}
          </div>

          {/* Row 4: Stats */}
          <StatGrid
            baseStats={attacker.baseStats} evs={attacker.evs} ivs={attacker.ivs}
            boosts={attacker.boosts} nature={attacker.nature} level={attacker.level}
            side="attacker" onEvStep={onEvStep} onEvInput={onEvInput} onIvInput={onIvInput} onBoostStep={onBoostStep}
            highlightStat={attackerHighlight}
          />

          <EvToolsPanel
            attacker={attacker}
            defenders={defenders}
            gen={gen}
            onApplyEvs={onApplyEvs}
          />
        </>
      )}

      {!attacker.baseStats && (
        <div className="qc-panel-placeholder">Select your Pokémon to begin</div>
      )}
    </div>
  );
}

// ── Defender Card ─────────────────────────────────────────────────────────────

function DefenderCard({ defender, index, gen, attackerMove, onSelect, onClear, onRemove, onUpdate, onImport }) {
  const [showFullStats, setShowFullStats] = useState(false);
  const [pasteOpen, setPasteOpen]         = useState(false);
  const [pasteText, setPasteText]         = useState('');
  const movePool = usePokemonMoves(defender.pokemonKey, gen);
  const genObj   = useMemo(() => getGen(gen), [gen]);
  const preset = defender.pokemonKey ? COMMON_SETS[defender.pokemonKey] : null;
  const attackerMoveCategory = MOVE_DATA[attackerMove]?.category;
  const defenderHighlight = attackerMoveCategory === 'Physical' ? 'def' : attackerMoveCategory === 'Special' ? 'spd' : null;

  const handleLoadSet = () => {
    if (!preset) return;
    onUpdate({
      nature:  preset.nature  || defender.nature,
      item:    preset.item    || defender.item,
      ability: preset.ability || defender.ability,
      evs:     preset.evs ? { ...preset.evs } : defender.evs,
    });
  };

  const handlePaste = text => {
    const parsed = parseShowdownSet(text);
    if (parsed) { onImport(defender.id, parsed); setPasteText(''); setPasteOpen(false); }
  };

  const updateEv = (stat, raw) => {
    const val = Math.min(252, Math.max(0, Number(raw) || 0));
    onUpdate({ evs: { ...defender.evs, [stat]: val } });
  };

  const opts  = defender.moveOpts || DEFAULT_MOVE_OPTS();
  const moves = defender.moves    || ['', '', '', ''];

  const updateMoveOpt = (i, patch) => {
    const next = opts.map((o, idx) => idx === i ? { ...o, ...patch } : o);
    onUpdate({ moveOpts: next });
  };

  return (
    <div className="qc-defender-card" style={defender.types?.[0] ? { '--panel-accent': TYPE_COLORS[defender.types[0]] } : {}}>
      {/* Row 1: vs label + sprite inline + search + remove */}
      <div className="qc-panel-header-row">
        <span className="qc-defender-vs">vs {index + 1}</span>
        {defender.sprite && (
          <img className="qc-panel-sprite-inline" src={defender.sprite} alt={defender.showdownName} />
        )}
        <div className="qc-panel-header-info">
          <PokemonSearch
            selectedKey={defender.pokemonKey}
            onSelect={name => onSelect(defender.id, name)}
            onClear={onClear}
            placeholder="Search opponent…"
            types={defender.types}
          />
          {defender.baseStats && <NatureBadge nature={defender.nature} />}
        </div>
        <button className="qc-defender-remove" onClick={onRemove} title="Remove target">✕</button>
      </div>

      <div className="qc-panel-toolbar">
        {preset && (
          <button className="qc-quick-set-btn compact" onClick={handleLoadSet}>
            Quick Set — {preset.nature} · {preset.item}
          </button>
        )}
        <button className="qc-paste-btn" onClick={() => setPasteOpen(o => !o)}
          data-tip="Paste a Showdown export set to auto-fill all fields">
          {pasteOpen ? '✕' : '📋'}
        </button>
      </div>

      {pasteOpen && (
        <div className="qc-paste-wrap">
          <textarea className="qc-paste-area"
            value={pasteText}
            placeholder={"Paste Showdown set here…"}
            onChange={e => setPasteText(e.target.value)}
            onPaste={e => {
              e.preventDefault();
              const text = e.clipboardData.getData('text');
              setPasteText(text);
              handlePaste(text);
            }}
          />
          {pasteText && (
            <button className="qc-paste-apply-btn" onClick={() => handlePaste(pasteText)}>Apply</button>
          )}
        </div>
      )}

      {defender.baseStats && (
        <>
          {/* Row 2: 4 move slots with per-move crit/hits opts */}
          <div className="qc-move-slots">
            {moves.map((mv, i) => {
              const isActive = mv !== '' && mv === defender.move;
              const currentNotInPool = mv && Array.isArray(movePool) && !movePool.includes(mv);
              const opt = opts[i] || { crit: false, hits: null };
              const { alwaysCrit, fixedHits, isMultiHit } = mv ? getMoveProps(mv, genObj) : { alwaysCrit: false, fixedHits: null, isMultiHit: false };
              return (
                <div key={i} className={`qc-move-slot-cell${isActive ? ' active' : ''}`}>
                  <select
                    className="qc-move-slot-select"
                    value={mv}
                    onChange={e => {
                      const newMoves = [...moves];
                      newMoves[i] = e.target.value;
                      const updates = { moves: newMoves };
                      if (e.target.value) {
                        updates.move = e.target.value;
                      } else if (mv === defender.move) {
                        updates.move = '';
                      }
                      onUpdate(updates);
                    }}
                  >
                    {!!defender.pokemonKey && !Array.isArray(movePool) ? (
                      <option value="">Loading…</option>
                    ) : (
                      <option value="">{`Move ${i + 1}`}</option>
                    )}
                    {currentNotInPool && <option value={mv}>{mv}</option>}
                    {Array.isArray(movePool) ? (
                      movePool.map(m => <option key={m} value={m}>{m}</option>)
                    ) : (
                      <optgroup label="All Competitive Moves">
                        {ALL_MOVE_NAMES.map(m => <option key={m} value={m}>{m}</option>)}
                      </optgroup>
                    )}
                  </select>
                  {mv && (
                    <div className="qc-move-opts">
                      <button
                        className={`qc-crit-btn${alwaysCrit ? ' locked' : opt.crit ? ' active' : ''}`}
                        onClick={alwaysCrit ? undefined : () => updateMoveOpt(i, { crit: !opt.crit })}
                        title={alwaysCrit ? 'Always crits — inherent to this move' : 'Force critical hit'}
                        disabled={alwaysCrit}
                      >★ Crit</button>
                      {fixedHits !== null && (
                        <span className="qc-fixed-hits-badge" title="Always hits this many times">×{fixedHits}</span>
                      )}
                      {fixedHits === null && isMultiHit && (
                        <select className="qc-hits-sel"
                          value={opt.hits || ''}
                          onChange={e => updateMoveOpt(i, { hits: e.target.value ? Number(e.target.value) : null })}
                          title="Override hit count">
                          <option value="">×avg</option>
                          <option value="2">×2</option>
                          <option value="3">×3</option>
                          <option value="4">×4</option>
                          <option value="5">×5</option>
                        </select>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Row 3a: Nature / Item / Ability */}
          <div className="qc-config-inline">
            <div className="qc-field">
              <label>Nature</label>
              <select value={defender.nature} onChange={e => onUpdate({ nature: e.target.value })}>
                {NATURES.map(n => {
                  const nat = NATURE_TABLE[n];
                  const label = nat?.boost
                    ? `${n} (+${SMOGON_TO_LABEL[nat.boost]}/-${SMOGON_TO_LABEL[nat.reduce]})`
                    : `${n} (neutral)`;
                  return <option key={n} value={n}>{label}</option>;
                })}
              </select>
            </div>
            <div className="qc-field">
              <label>Item</label>
              {isMegaKey(defender.pokemonKey) ? (
                <div className="qc-mega-stone-locked" title="Mega Pokémon must hold their Mega Stone">
                  <span className="qc-mega-stone-name">{defender.item}</span>
                  <span className="qc-mega-stone-lock">🔒 Mega Stone</span>
                </div>
              ) : (
                <select value={defender.item} onChange={e => onUpdate({ item: e.target.value })}>
                  {Object.entries(ITEM_GROUPS).map(([group, items]) => (
                    <optgroup key={group} label={group}>
                      {items.map(i => <option key={i} value={i}>{i}</option>)}
                    </optgroup>
                  ))}
                </select>
              )}
            </div>
            <div className="qc-field">
              <label>Ability</label>
              <input type="text" value={defender.ability}
                placeholder="Ability" list={`def-${defender.id}-ability-list`}
                onChange={e => onUpdate({ ability: e.target.value })} />
              {defender.knownAbilities?.length > 0 && (
                <datalist id={`def-${defender.id}-ability-list`}>
                  {defender.knownAbilities.map(a => <option key={a} value={a} />)}
                </datalist>
              )}
              {ABILITY_ON_ABILITIES.has(defender.ability) && (
                <label className="qc-ability-on-check" title="Toggle whether this ability's conditional effect is active">
                  <input type="checkbox" checked={defender.abilityOn}
                    onChange={e => onUpdate({ abilityOn: e.target.checked })} />
                  Active
                </label>
              )}
            </div>
          </div>

          {/* Row 3b: Tera / Status / flags */}
          <div className="qc-config-inline qc-config-secondary">
            <div className="qc-field">
              <label>Tera</label>
              <select value={defender.teraType} onChange={e => onUpdate({ teraType: e.target.value })}>
                <option value="">No Tera</option>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="qc-field">
              <label>Status</label>
              <select value={defender.status} onChange={e => onUpdate({ status: e.target.value })}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'None'}</option>)}
              </select>
            </div>
            {gen === 9 && defender.teraType && (
              <label className="qc-flag-label" data-tip="Activates Terastallize: changes opponent's type to their Tera type for this calc"
                style={{ alignSelf: 'flex-end', marginBottom: '3px' }}>
                <input type="checkbox" checked={defender.isTerastallized}
                  onChange={e => onUpdate({ isTerastallized: e.target.checked })} />
                Tera'd
              </label>
            )}
          </div>

          {/* Compact EV row — shows final stat beside each input */}
          <div className="qc-defender-evs">
            {STAT_KEYS.map(k => (
              <div key={k} className={`qc-def-ev-cell${k === defenderHighlight ? ' qc-stat-relevant-cell' : ''}`}>
                <label>{statLabel(k)}</label>
                <input type="number" min={0} max={252} value={defender.evs[k]}
                  onChange={e => updateEv(k, e.target.value)} />
                <span className="qc-def-final">
                  {defender.baseStats[k] ? calcStat(k, defender.baseStats[k], defender.evs[k], defender.ivs[k], defender.level, defender.nature) : '—'}
                </span>
              </div>
            ))}
          </div>

          <button className="qc-stats-toggle" onClick={() => setShowFullStats(s => !s)}>
            {showFullStats ? '▲ Hide Full Stats' : '▼ Full Stats + Boosts'}
          </button>

          {showFullStats && (
            <StatGrid
              baseStats={defender.baseStats} evs={defender.evs} ivs={defender.ivs}
              boosts={defender.boosts} nature={defender.nature} level={defender.level}
              side={`def-${defender.id}`}
              onEvStep={(_, stat, dir) => updateEv(stat, Math.min(252, Math.max(0, defender.evs[stat] + dir * 4)))}
              onEvInput={(_, stat, v) => updateEv(stat, v)}
              onIvInput={(_, stat, v) => onUpdate({ ivs: { ...defender.ivs, [stat]: Math.min(31, Math.max(0, Number(v) || 0)) } })}
              onBoostStep={(_, stat, dir) => onUpdate({ boosts: { ...defender.boosts, [stat]: Math.min(6, Math.max(-6, (defender.boosts[stat] || 0) + dir)) } })}
              highlightStat={defenderHighlight}
            />
          )}
        </>
      )}

      {!defender.baseStats && (
        <div className="qc-panel-placeholder" style={{ padding: '1rem 0' }}>Select a target Pokémon</div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function QuickCalc() {
  const [gen, setGen]           = useState(9);
  const [regulation, setRegulation] = useState(null);
  const [level, setLevel]       = useState(50);
  const [attacker, setAttacker] = useState(defaultSide);
  const [attackerMove, setAttackerMove] = useState('');
  const [defenders, setDefenders] = useState(() => [defaultDefender()]);
  const [fieldConfig, setFieldConfig] = useState(defaultField);
  const [resultsOut, setResultsOut] = useState({}); // { [defId]: calcResult }
  const [resultsIn,  setResultsIn]  = useState({}); // { [defId]: calcResult }
  // ── Attacker updaters ──────────────────────────────────────────────────────
  const handleLevelChange = useCallback(val => {
    const lvl = Math.max(1, Math.min(100, Number(val) || 50));
    setLevel(lvl);
    setAttacker(prev => ({ ...prev, level: lvl }));
    setDefenders(prev => prev.map(d => ({ ...d, level: lvl })));
  }, []);

  const updateAttacker = useCallback(patch => setAttacker(prev => ({ ...prev, ...patch })), []);
  const updateField    = useCallback(patch => setFieldConfig(prev => ({ ...prev, ...patch })), []);
  const updateAttackerSide = useCallback(patch => setFieldConfig(prev => ({ ...prev, attackerSide: { ...prev.attackerSide, ...patch } })), []);
  const updateDefenderSide = useCallback(patch => setFieldConfig(prev => ({ ...prev, defenderSide: { ...prev.defenderSide, ...patch } })), []);

  const stepEv = useCallback((side, stat, dir) => {
    if (side !== 'attacker') return;
    setAttacker(prev => ({ ...prev, evs: { ...prev.evs, [stat]: Math.min(252, Math.max(0, prev.evs[stat] + dir * 4)) } }));
  }, []);

  const updateEv = useCallback((side, stat, raw) => {
    if (side !== 'attacker') return;
    const val = Math.min(252, Math.max(0, Number(raw) || 0));
    setAttacker(prev => ({ ...prev, evs: { ...prev.evs, [stat]: val } }));
  }, []);

  const updateIv = useCallback((side, stat, raw) => {
    if (side !== 'attacker') return;
    const val = Math.min(31, Math.max(0, Number(raw) || 0));
    setAttacker(prev => ({ ...prev, ivs: { ...prev.ivs, [stat]: val } }));
  }, []);

  const updateBoost = useCallback((side, stat, dir) => {
    if (side !== 'attacker') return;
    setAttacker(prev => ({ ...prev, boosts: { ...prev.boosts, [stat]: Math.min(6, Math.max(-6, prev.boosts[stat] + dir)) } }));
  }, []);

  // ── Pokémon selection ──────────────────────────────────────────────────────
  const loadPreset = (preset, current) => preset ? {
    nature:  preset.nature  || current.nature,
    item:    preset.item    || current.item,
    ability: preset.ability || current.ability,
    evs:     preset.evs ? { ...preset.evs } : current.evs,
  } : {};

  const handleSelectAttacker = useCallback(name => {
    // Mega keys are direct POKEMON_DATA keys; regular keys go through resolveKey
    const key  = isMegaKey(name) ? name : resolveKey(name);
    const data = POKEMON_DATA[key];
    if (!data) return;
    const sprite = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${data.id}.png`;
    const megaStone = getMegaStone(key);
    setAttacker(prev => ({
      ...prev, pokemonKey: key, showdownName: toShowdownName(key),
      baseStats: data.baseStats, sprite,
      types: data.types || [], knownAbilities: data.abilities || [],
      ...(megaStone ? { item: megaStone } : {}),
      ...(!megaStone ? loadPreset(COMMON_SETS[key] || COMMON_SETS[name], prev) : {}),
    }));
    setResultsOut({}); setResultsIn({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectDefender = useCallback((id, name) => {
    const key  = isMegaKey(name) ? name : resolveKey(name);
    const data = POKEMON_DATA[key];
    if (!data) return;
    const sprite = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${data.id}.png`;
    const megaStone = getMegaStone(key);
    setDefenders(prev => prev.map(d => d.id !== id ? d : {
      ...d, pokemonKey: key, showdownName: toShowdownName(key),
      baseStats: data.baseStats, sprite,
      types: data.types || [], knownAbilities: data.abilities || [],
      ...(megaStone ? { item: megaStone } : {}),
      ...(!megaStone ? loadPreset(COMMON_SETS[key] || COMMON_SETS[name], d) : {}),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearDefender = useCallback(id => {
    setDefenders(prev => prev.map(d => d.id === id ? { ...defaultSide(), id, move: '' } : d));
  }, []);

  // ── Paste import handlers ──────────────────────────────────────────────────
  const handleImportAttacker = useCallback(parsed => {
    const key  = isMegaKey(parsed.pokemonKey) ? parsed.pokemonKey : resolveKey(parsed.pokemonKey);
    const data = POKEMON_DATA[key];
    if (!data) return;
    const sprite = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${data.id}.png`;
    const megaStone = getMegaStone(key);
    setAttacker({
      ...defaultSide(),
      pokemonKey: key, showdownName: toShowdownName(key),
      baseStats: data.baseStats, sprite,
      types: data.types || [], knownAbilities: data.abilities || [],
      nature: parsed.nature, item: megaStone || parsed.item, ability: parsed.ability,
      teraType: parsed.teraType, evs: { ...parsed.evs }, ivs: { ...parsed.ivs },
      moves: [...parsed.moves],
    });
    if (parsed.moves[0]) setAttackerMove(parsed.moves[0]);
    setResultsOut({}); setResultsIn({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImportDefender = useCallback((id, parsed) => {
    const key  = isMegaKey(parsed.pokemonKey) ? parsed.pokemonKey : resolveKey(parsed.pokemonKey);
    const data = POKEMON_DATA[key];
    if (!data) return;
    const sprite = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${data.id}.png`;
    const megaStone = getMegaStone(key);
    setDefenders(prev => prev.map(d => d.id !== id ? d : {
      ...defaultSide(),
      id, move: parsed.moves[0] || '',
      pokemonKey: key, showdownName: toShowdownName(key),
      baseStats: data.baseStats, sprite,
      types: data.types || [], knownAbilities: data.abilities || [],
      nature: parsed.nature, item: megaStone || parsed.item, ability: parsed.ability,
      teraType: parsed.teraType, evs: { ...parsed.evs }, ivs: { ...parsed.ivs },
      moves: [...parsed.moves],
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDefender = useCallback((id, patch) => {
    setDefenders(prev => prev.map(d => d.id === id ? { ...d, ...patch } : d));
  }, []);

  const addDefender = useCallback(() => {
    setDefenders(prev => prev.length < MAX_DEFENDERS ? [...prev, { ...defaultDefender(), level }] : prev);
  }, [level]);

  const removeDefender = useCallback(id => {
    setDefenders(prev => {
      if (prev.length <= 1) return [{ ...defaultSide(), id: prev[0].id, move: '' }];
      return prev.filter(d => d.id !== id);
    });
  }, []);

  // ── Live calculations ──────────────────────────────────────────────────────
  // calcKey is a stable string — only fields that affect damage trigger recalcs
  const calcKey = useMemo(() => {
    const ak = sideCalcKey(attacker);
    const atkOptKey = (attacker.moveOpts||[]).map(o=>`${o?.crit?1:0}:${o?.hits||0}`).join(',');
    const dks = defenders.map(d => {
      const defOptKey = (d.moveOpts||[]).map(o=>`${o?.crit?1:0}:${o?.hits||0}`).join(',');
      return `${d.id}~${sideCalcKey(d)}~${(d.moves||[]).join(',')}~${defOptKey}`;
    }).join('||');
    return [gen, ak, (attacker.moves||[]).join(','), atkOptKey, dks, JSON.stringify(fieldConfig)].join(':::');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gen, attacker, defenders, fieldConfig]);

  const attackerRef  = useRef(attacker);  attackerRef.current  = attacker;
  const defendersRef = useRef(defenders); defendersRef.current = defenders;
  const fieldRef     = useRef(fieldConfig); fieldRef.current   = fieldConfig;

  useEffect(() => {
    const a    = attackerRef.current;
    const defs = defendersRef.current;
    const f    = fieldRef.current;
    if (!a.baseStats) { setResultsOut({}); setResultsIn({}); return; }

    const genObj     = getGen(gen);
    const aPokemon   = buildPokemon(genObj, a);
    const fieldObj   = buildField(f);
    const newOut = {};
    const newIn  = {};

    // For incoming damage (opponent attacks you), attacker/defender sides are swapped.
    const fieldObjIn = fieldObj.clone().swap();

    const atkMoves    = a.moves    || ['', '', '', ''];
    const atkMoveOpts = a.moveOpts || [];
    defs.forEach(def => {
      if (!def.baseStats) return;
      const dPokemon   = buildPokemon(genObj, def);
      const defMoveOpts = def.moveOpts || [];
      atkMoves.forEach((mv, mi) => {
        if (!mv) return;
        if (!newOut[def.id]) newOut[def.id] = [];
        try {
          const o = atkMoveOpts[mi] || {};
          const moveArgs = {};
          if (o.crit) moveArgs.isCrit = true;
          if (o.hits && o.hits > 1) moveArgs.hits = o.hits;
          const moveObj = new Move(genObj, mv, moveArgs);
          // Charge doubles the power of the attacker's next Electric move
          if (f.attackerSide.isCharged && moveObj.type === 'Electric') moveObj.bp *= 2;
          newOut[def.id][mi] = calculate(genObj, aPokemon, dPokemon, moveObj, fieldObj);
        } catch { newOut[def.id][mi] = 'error'; }
      });
      const defMoves = def.moves || ['', '', '', ''];
      defMoves.forEach((mv, mi) => {
        if (!mv) return;
        if (!newIn[def.id]) newIn[def.id] = [];
        try {
          const o = defMoveOpts[mi] || {};
          const moveArgs = {};
          if (o.crit) moveArgs.isCrit = true;
          if (o.hits && o.hits > 1) moveArgs.hits = o.hits;
          const moveObj = new Move(genObj, mv, moveArgs);
          // For incoming calcs the opponent is the attacker — check their Charge status
          if (f.defenderSide.isCharged && moveObj.type === 'Electric') moveObj.bp *= 2;
          newIn[def.id][mi] = calculate(genObj, dPokemon, aPokemon, moveObj, fieldObjIn);
        } catch { newIn[def.id][mi] = 'error'; }
      });
    });

    setResultsOut(newOut);
    setResultsIn(newIn);
  }, [calcKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Apply EV patch from EvToolsPanel ──────────────────────────────────────
  const applyAttackerPatch = useCallback(patch => {
    setAttacker(prev => ({ ...prev, ...patch }));
    // No need to clear results — the calc useEffect re-runs automatically
    // when calcKey changes (EVs changed → JSON.stringify(evs) differs).
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="qc-page">
      <div className="qc-page-header">
        <h1>Damage Calculator</h1>
      </div>

      <FieldPanel
        gen={gen} regulation={regulation} fieldConfig={fieldConfig} level={level}
        onGenChange={setGen} onRegulationChange={setRegulation}
        onFieldUpdate={updateField} onLevelChange={handleLevelChange}
        onAttackerSideUpdate={updateAttackerSide}
        onDefenderSideUpdate={updateDefenderSide}
      />

      <DamageResults
        attacker={attacker}
        defenders={defenders}
        attackerMove={attackerMove}
        resultsOut={resultsOut}
        resultsIn={resultsIn}
      />

      <div className="qc-main-grid">
        <AttackerPanel
          attacker={attacker} gen={gen}
          attackerMove={attackerMove} defenders={defenders}
          onSelect={handleSelectAttacker}
          onClear={() => { setAttacker({ ...defaultSide(), level }); setAttackerMove(''); setResultsOut({}); setResultsIn({}); }}
          onUpdate={updateAttacker}
          onEvStep={stepEv} onEvInput={updateEv} onIvInput={updateIv} onBoostStep={updateBoost}
          onMoveChange={setAttackerMove}
          onApplyEvs={applyAttackerPatch}
          onImport={handleImportAttacker}
        />

        <div className="qc-defenders-column">
          {defenders.map((def, i) => (
            <DefenderCard
              key={def.id}
              defender={def} index={i} gen={gen}
              attackerMove={attackerMove}
              onSelect={handleSelectDefender}
              onClear={() => handleClearDefender(def.id)}
              onRemove={() => removeDefender(def.id)}
              onUpdate={patch => updateDefender(def.id, patch)}
              onImport={handleImportDefender}
            />
          ))}

          {defenders.length < MAX_DEFENDERS && (
            <button className="qc-add-defender" onClick={addDefender}>
              + Add Target ({defenders.length}/{MAX_DEFENDERS})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
